import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayIcon, HeartIcon, MagnifyingGlassIcon, AdjustmentsHorizontalIcon, ShareIcon, ChatBubbleLeftIcon, EyeIcon, ChevronDownIcon, ChevronRightIcon, XMarkIcon, StarIcon, ClockIcon, ArrowTrendingUpIcon, ChartBarIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { ApiService, Work } from '../../services/api';
import { useWorks } from '../../contexts/WorksContext';
import { useLanguage } from '../../contexts/LanguageContext';

interface CategoryItem {
  key: string;
  label: string;
  labelEn: string;
  children?: CategoryItem[];
}

interface RegularLicensePlan {
  price: number;
  name: string;
  features: string[];
  color: string;
  icon: string;
}

interface PioneerLicensePlan {
  price: number;
  name: string;
  features: string[];
  color: string;
  icon: string;
  rewardPerPurchase: number;
}

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  work: any;
  language: string;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({ isOpen, onClose, work, language }) => {
  const [selectedLicense, setSelectedLicense] = useState<'basic' | 'standard' | 'premium'>('basic');
  const [quantity, setQuantity] = useState(1);

  if (!isOpen) return null;

  const showPioneerFeatures = work?.isPioneer && work?.type === 'full-version';

  // 普通版权许可计划（非先行者作品）
  const regularLicensePlans: Record<string, RegularLicensePlan> = {
    basic: {
      price: 88,
      name: language === 'zh' ? '基础许可' : 'Basic License',
      features: [
        language === 'zh' ? '数字下载使用权' : 'Digital Download Rights',
        language === 'zh' ? '个人项目使用' : 'Personal Project Use',
        language === 'zh' ? '高品质音频文件' : 'High Quality Audio File',
        language === 'zh' ? '永久使用权' : 'Lifetime Usage Rights'
      ],
      color: 'blue',
      icon: '🎵'
    },
    standard: {
      price: 188,
      name: language === 'zh' ? '标准许可' : 'Standard License',
      features: [
        language === 'zh' ? '包含基础许可所有内容' : 'All Basic License Features',
        language === 'zh' ? '商业复制使用权' : 'Commercial Reproduction Rights',
        language === 'zh' ? '音频编辑授权' : 'Audio Editing License',
        language === 'zh' ? '社交媒体发布权' : 'Social Media Publishing Rights',
        language === 'zh' ? '背景音乐使用' : 'Background Music Usage'
      ],
      color: 'purple',
      icon: '🎼'
    },
    premium: {
      price: 388,
      name: language === 'zh' ? '高级许可' : 'Premium License',
      features: [
        language === 'zh' ? '包含标准许可所有内容' : 'All Standard License Features',
        language === 'zh' ? '公开表演授权' : 'Public Performance License',
        language === 'zh' ? '广播媒体使用权' : 'Broadcasting Rights',
        language === 'zh' ? '商业演出授权' : 'Commercial Performance License',
        language === 'zh' ? '无限次使用许可' : 'Unlimited Usage License',
        language === 'zh' ? '专属客服支持' : 'Dedicated Support'
      ],
      color: 'gold',
      icon: '👑'
    }
  };

  // 先行者版权许可计划（带返利功能）
  const pioneerLicensePlans: Record<string, PioneerLicensePlan> = {
    basic: {
      price: 88,
      name: language === 'zh' ? '基础先行者' : 'Basic Pioneer',
      features: [
        language === 'zh' ? '数字下载使用权' : 'Digital Download Rights',
        language === 'zh' ? '个人项目使用' : 'Personal Project Use',
        language === 'zh' ? '高品质音频文件' : 'High Quality Audio File',
        language === 'zh' ? '永久使用权' : 'Lifetime Usage Rights',
        language === 'zh' ? '先行者身份认证' : 'Pioneer Status Badge',
        language === 'zh' ? '后续购买者返利' : 'Referral Rewards from Future Buyers'
      ],
      color: 'blue',
      icon: '⭐',
      rewardPerPurchase: 13
    },
    standard: {
      price: 188,
      name: language === 'zh' ? '标准先行者' : 'Standard Pioneer',
      features: [
        language === 'zh' ? '包含基础先行者所有权益' : 'All Basic Pioneer Features',
        language === 'zh' ? '商业复制使用权' : 'Commercial Reproduction Rights',
        language === 'zh' ? '音频编辑授权' : 'Audio Editing License',
        language === 'zh' ? '社交媒体发布权' : 'Social Media Publishing Rights',
        language === 'zh' ? '优先新作品体验' : 'Priority New Release Access',
        language === 'zh' ? '更高返利比例' : 'Higher Referral Rate'
      ],
      color: 'purple',
      icon: '👑',
      rewardPerPurchase: 28
    },
    premium: {
      price: 388,
      name: language === 'zh' ? '高级先行者' : 'Premium Pioneer',
      features: [
        language === 'zh' ? '包含标准先行者所有权益' : 'All Standard Pioneer Features',
        language === 'zh' ? '公开表演授权' : 'Public Performance License',
        language === 'zh' ? '广播媒体使用权' : 'Broadcasting Rights',
        language === 'zh' ? '商业演出授权' : 'Commercial Performance License',
        language === 'zh' ? '无限次使用许可' : 'Unlimited Usage License',
        language === 'zh' ? '专属客服支持' : 'Dedicated Support',
        language === 'zh' ? '最高返利比例' : 'Maximum Referral Rate'
      ],
      color: 'gold',
      icon: '💎',
      rewardPerPurchase: 58
    }
  };

  const currentPlans = showPioneerFeatures ? pioneerLicensePlans : regularLicensePlans;
  const currentPlan = currentPlans[selectedLicense];
  const unitPrice = currentPlan.price;
  const totalPrice = unitPrice * quantity;

  // 类型守卫函数
  const isPioneerPlan = (plan: RegularLicensePlan | PioneerLicensePlan): plan is PioneerLicensePlan => {
    return 'rewardPerPurchase' in plan;
  };

  const getColorClasses = (color: string, isSelected: boolean) => {
    const colorMap = {
      blue: isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300',
      purple: isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300',
      gold: isSelected ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-yellow-300'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        
        <div className={`relative text-white px-6 py-4 flex items-center justify-between flex-shrink-0 ${
          showPioneerFeatures 
            ? 'bg-gradient-to-r from-purple-600 to-blue-600' 
            : 'bg-gradient-to-r from-blue-600 to-gray-600'
        }`}>
          <div className="flex items-center gap-3">
            <div className="text-2xl">{showPioneerFeatures ? '⭐' : '🎵'}</div>
            <div>
              <h2 className="font-bold text-lg">
                {showPioneerFeatures 
                  ? (language === 'zh' ? '先行者版权许可' : 'Pioneer Copyright License')
                  : (language === 'zh' ? '版权许可购买' : 'Copyright License Purchase')
                }
              </h2>
              <p className={`text-sm ${showPioneerFeatures ? 'text-purple-100' : 'text-blue-100'}`}>
                {work?.title}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white hover:bg-opacity-20 rounded">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-lg p-4 sticky top-0">
                <img src={work?.coverUrl} alt={work?.title} className="w-full h-32 rounded object-cover mb-3" />
                <h3 className="font-bold text-sm mb-1">{work?.title}</h3>
                <p className="text-xs text-gray-600 mb-2">{work?.artist || ''}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">{work?.genre}</span>
                  {work?.duration && (
                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">{work?.duration}</span>
                  )}
                </div>
                
                {showPioneerFeatures ? (
                  <div className="bg-gradient-to-r from-purple-100 to-blue-100 p-3 rounded text-xs text-center">
                    <StarIcon className="w-5 h-5 mx-auto mb-2 text-purple-600" />
                    <div className="font-bold text-purple-800">{language === 'zh' ? '先行者作品' : 'Pioneer Work'}</div>
                    <div className="text-purple-600 mt-1">{language === 'zh' ? '早买早受益' : 'Early Buyers Benefit More'}</div>
                  </div>
                ) : (
                  <div className="bg-gray-100 p-3 rounded text-xs text-center">
                    <div className="font-bold text-gray-800">{language === 'zh' ? '标准作品' : 'Standard Work'}</div>
                    <div className="text-gray-600 mt-1">{language === 'zh' ? '版权许可使用' : 'Copyright License Usage'}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-3">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                {showPioneerFeatures ? <StarIcon className="w-6 h-6 text-purple-600" /> : <CurrencyDollarIcon className="w-6 h-6 text-blue-600" />}
                {showPioneerFeatures 
                  ? (language === 'zh' ? '选择先行者等级' : 'Choose Pioneer Level')
                  : (language === 'zh' ? '选择许可计划' : 'Choose License Plan')
                }
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {Object.entries(currentPlans).map(([key, plan]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedLicense(key as any)}
                    className={`p-6 rounded-xl border-2 transition-all text-left h-full ${
                      getColorClasses(plan.color, selectedLicense === key)
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="text-2xl">{plan.icon}</div>
                      <div>
                        <h4 className="font-bold text-lg">{plan.name}</h4>
                        <div className="text-2xl font-bold text-gray-900">
                          {work?.currency || 'HK$'} {plan.price}
                        </div>
                      </div>
                    </div>
                    
                    {showPioneerFeatures && isPioneerPlan(plan) && (
                      <div className="bg-white bg-opacity-60 rounded-lg p-3 mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs text-gray-600">
                            {language === 'zh' ? '每次后续购买返利' : 'Reward per Future Purchase'}
                          </span>
                          <span className="font-bold text-green-600">
                            {`+${work?.currency || 'HK$'} ${plan.rewardPerPurchase}`}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {language === 'zh' ? '有人购买相同等级即可获得' : 'Earned when someone buys same level'}
                        </div>
                      </div>
                    )}
                    
                    <ul className="space-y-2 text-sm">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-green-500 mt-0.5">✓</span>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">
                      {language === 'zh' ? '购买数量' : 'Purchase Quantity'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {showPioneerFeatures 
                        ? (language === 'zh' ? '每份许可包含使用权和先行者权益' : 'Each license includes usage rights and pioneer benefits')
                        : (language === 'zh' ? '每个许可证可用于一个项目' : 'Each license valid for one project')
                      }
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 bg-white border border-gray-300 rounded-lg font-bold hover:bg-gray-100 transition-colors"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-bold text-xl">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 bg-white border border-gray-300 rounded-lg font-bold hover:bg-gray-100 transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              <div className={`rounded-lg p-6 border ${
                showPioneerFeatures 
                  ? 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200' 
                  : 'bg-gradient-to-r from-blue-50 to-gray-50 border-blue-200'
              }`}>
                <h4 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  {showPioneerFeatures ? <StarIcon className="w-5 h-5 text-purple-600" /> : <CurrencyDollarIcon className="w-5 h-5 text-blue-600" />}
                  {showPioneerFeatures 
                    ? (language === 'zh' ? '先行者权益摘要' : 'Pioneer Benefits Summary')
                    : (language === 'zh' ? '购买摘要' : 'Purchase Summary')
                  }
                </h4>
                
                <div className="border-t pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg">{language === 'zh' ? '总支付金额' : 'Total Payment'}</span>
                    <span className={`font-bold text-2xl ${showPioneerFeatures ? 'text-purple-600' : 'text-blue-600'}`}>
                      {work?.currency || 'HK$'} {totalPrice.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4">
                  <h5 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2">
                    {showPioneerFeatures ? '🎯' : '📋'} {language === 'zh' ? '您将获得' : 'You Will Get'}
                  </h5>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-500">📄</span>
                      <span>{language === 'zh' ? '数字许可证书' : 'Digital License Certificate'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-500">🔒</span>
                      <span>{language === 'zh' ? '法律权益保护' : 'Legal Protection'}</span>
                    </div>
                    {showPioneerFeatures ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-purple-500">⭐</span>
                          <span>{language === 'zh' ? '先行者身份认证' : 'Pioneer Status Badge'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-500">💰</span>
                          <span>{language === 'zh' ? '后续购买返利' : 'Future Purchase Rewards'}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-blue-500">📧</span>
                          <span>{language === 'zh' ? '邮件确认' : 'Email Confirmation'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-500">⏰</span>
                          <span>{language === 'zh' ? '即时激活' : 'Instant Activation'}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t bg-gray-50 p-6 flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            {language === 'zh' ? '取消' : 'Cancel'}
          </button>
          <button
            onClick={() => {
              const purchaseType = showPioneerFeatures ? (language === 'zh' ? '先行者' : 'Pioneer') : (language === 'zh' ? '标准' : 'Standard');
              const rewardText = showPioneerFeatures && isPioneerPlan(currentPlan) 
                ? `\n${language === 'zh' ? '单次返利' : 'Per Purchase Reward'}: +${work?.currency || 'HK$'} ${currentPlan.rewardPerPurchase * quantity}` 
                : '';
              
              alert(
                `${purchaseType}${language === 'zh' ? '购买确认' : ' Purchase Confirmation'}\n\n` +
                `${language === 'zh' ? '作品' : 'Work'}: ${work?.title}\n` +
                `${language === 'zh' ? '等级' : 'Level'}: ${currentPlan.name}\n` +
                `${language === 'zh' ? '数量' : 'Quantity'}: ${quantity}\n` +
                `${language === 'zh' ? '总价' : 'Total'}: ${work?.currency || 'HK$'} ${totalPrice.toLocaleString()}${rewardText}\n\n` +
                `${language === 'zh' ? '购买成功！功能开发中，敬请期待！' : 'Purchase successful! Feature coming soon!'}`
              );
              onClose();
            }}
            className={`flex-2 py-3 px-8 text-white rounded-lg transition-all font-medium flex items-center justify-center gap-2 ${
              showPioneerFeatures
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                : 'bg-gradient-to-r from-blue-600 to-gray-600 hover:from-blue-700 hover:to-gray-700'
            }`}
          >
            {showPioneerFeatures ? <StarIcon className="w-5 h-5" /> : <CurrencyDollarIcon className="w-5 h-5" />}
            {showPioneerFeatures 
              ? `${language === 'zh' ? '成为先行者' : 'Become Pioneer'} - ${work?.currency || 'HK$'} ${totalPrice.toLocaleString()}`
              : `${language === 'zh' ? '立即购买' : 'Buy Now'} - ${work?.currency || 'HK$'} ${totalPrice.toLocaleString()}`
            }
          </button>
        </div>
      </div>
    </div>
  );
};

const WorksPage: React.FC = () => {
  const [works, setWorks] = useState<Work[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('full-version-top');
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['full-version', 'demo']);
  const [purchaseModal, setPurchaseModal] = useState<{ isOpen: boolean; work: any }>({ isOpen: false, work: null });
  const navigate = useNavigate();
  const { publishedWorks } = useWorks();
  const { t, language } = useLanguage();

  const categories: CategoryItem[] = useMemo(() => [
    {
      key: 'full-version',
      label: '完整版本',
      labelEn: 'Full Version',
      children: [
        { key: 'full-version-top', label: '热门', labelEn: 'Top' },
        { key: 'full-version-hot', label: '最新', labelEn: 'Hot' },
        { key: 'full-version-style', label: '风格', labelEn: 'Style' },
        { key: 'full-version-creator', label: '创作者', labelEn: 'Creator' }
      ]
    },
    {
      key: 'demo',
      label: 'Demo',
      labelEn: 'Demo',
      children: [
        { key: 'demo-top', label: '热门', labelEn: 'Top' },
        { key: 'demo-hot', label: '最新', labelEn: 'Hot' },
        { key: 'demo-style', label: '风格', labelEn: 'Style' },
        { key: 'demo-creator', label: '创作者', labelEn: 'Creator' }
      ]
    }
  ], []);

  // 模拟混合数据：有些是先行者，有些不是；有些是demo，有些是完整版
  const mockFullVersionWorks = useMemo(() => [
    {
      id: 1,
      title: language === 'zh' ? '夏日微风' : 'Summer Breeze',
      description: language === 'zh' ? '轻柔的夏日旋律，带来清凉的感受' : 'A gentle summer melody that brings a refreshing feeling',
      coverUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      playCount: 2400,
      collaborators: [language === 'zh' ? '陈小薇' : 'Chen Xiaowei', language === 'zh' ? '马克' : 'Marco'],
      likes: 856,
      comments: 142,
      duration: '04:31',
      genre: language === 'zh' ? '流行' : 'Pop',
      artist: language === 'zh' ? '陈小薇' : 'Chen Xiaowei',
      artistAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80',
      type: 'full-version' as any,
      price: 88,
      currency: 'HK$',
      isPioneer: true, // 先行者作品
      pioneersCount: 23
    },
    {
      id: 2,
      title: language === 'zh' ? '城市之光' : 'City Lights',
      description: language === 'zh' ? '都市夜晚的电子音乐' : 'Electronic music of urban nights',
      coverUrl: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      playCount: 1800,
      collaborators: [language === 'zh' ? '王小明' : 'Wang Xiaoming', language === 'zh' ? '李诗雨' : 'Li Shiyu'],
      likes: 623,
      comments: 98,
      duration: '03:45',
      genre: language === 'zh' ? '电子' : 'Electronic',
      artist: language === 'zh' ? '王小明' : 'Wang Xiaoming',
      artistAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80',
      type: 'full-version' as any,
      price: 88,
      currency: 'HK$',
      isPioneer: false // 普通作品
    },
    {
      id: 5,
      title: language === 'zh' ? '星空物语' : 'Starry Tale',
      description: language === 'zh' ? '浪漫的星空主题音乐' : 'Romantic starry sky themed music',
      coverUrl: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      playCount: 3200,
      collaborators: [language === 'zh' ? '张音乐' : 'Zhang Music'],
      likes: 789,
      comments: 156,
      duration: '05:12',
      genre: language === 'zh' ? '古典' : 'Classical',
      artist: language === 'zh' ? '张音乐' : 'Zhang Music',
      artistAvatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80',
      type: 'full-version' as any,
      price: 88,
      currency: 'HK$',
      isPioneer: true, // 先行者作品
      pioneersCount: 41
    }
  ], [language]);

  const mockDemoWorks = useMemo(() => [
    {
      id: 3,
      title: language === 'zh' ? '山海之间 (Demo)' : 'Between Mountains and Sea (Demo)',
      description: language === 'zh' ? '民谣风格的Demo版本' : 'Folk style demo version',
      coverUrl: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      playCount: 1200,
      collaborators: [language === 'zh' ? '张三和' : 'Zhang Sanhe'],
      likes: 450,
      comments: 89,
      duration: '02:30',
      genre: language === 'zh' ? '民谣' : 'Folk',
      artist: language === 'zh' ? '张三和' : 'Zhang Sanhe',
      artistAvatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80',
      type: 'demo' as any,
      price: 0,
      currency: '',
      isPioneer: false // Demo通常不是先行者
    },
    {
      id: 4,
      title: language === 'zh' ? '未来节拍 (Demo)' : 'Future Beat (Demo)',
      description: language === 'zh' ? '未来主义电子音乐Demo' : 'Futuristic electronic music demo',
      coverUrl: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      playCount: 890,
      collaborators: [language === 'zh' ? '电子乐队' : 'Electronic Band'],
      likes: 234,
      comments: 67,
      duration: '01:45',
      genre: language === 'zh' ? '电子' : 'Electronic',
      artist: language === 'zh' ? '电子乐队' : 'Electronic Band',
      artistAvatar: 'https://images.unsplash.com/photo-1527980965255-d3b416303d12?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80',
      type: 'demo' as any,
      price: 0,
      currency: '',
      isPioneer: false
    }
  ], [language]);

  useEffect(() => {
    loadWorks();
  }, [publishedWorks, language]);

  const loadWorks = async () => {
    setLoading(true);
    try {
      const response = await ApiService.getWorks(searchTerm);
      const allWorks = [...publishedWorks, ...response];
      setWorks(allWorks);
    } catch (error) {
      console.error('Failed to load works:', error);
      const allWorks = [...publishedWorks, ...mockFullVersionWorks, ...mockDemoWorks];
      setWorks(allWorks);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    await loadWorks();
  };

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryKey)
        ? prev.filter(key => key !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  const getCategoryLabel = (category: CategoryItem) => {
    return language === 'zh' ? category.label : category.labelEn;
  };

  const getFilteredWorks = () => {
    let filteredWorks = works;

    if (searchTerm) {
      filteredWorks = filteredWorks.filter(work => 
        work.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        work.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (work.artist && work.artist.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedCategory.startsWith('full-version')) {
      filteredWorks = [...mockFullVersionWorks];
    } else if (selectedCategory.startsWith('demo')) {
      filteredWorks = [...mockDemoWorks];
    }

    if (selectedCategory.includes('top')) {
      filteredWorks = filteredWorks.sort((a, b) => (b.playCount || 0) - (a.playCount || 0));
    } else if (selectedCategory.includes('hot')) {
      filteredWorks = filteredWorks.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    } else if (selectedCategory.includes('style')) {
      filteredWorks = filteredWorks.sort((a, b) => (a.genre || '').localeCompare(b.genre || ''));
    } else if (selectedCategory.includes('creator')) {
      filteredWorks = filteredWorks.sort((a, b) => (a.artist || '').localeCompare(b.artist || ''));
    }

    return filteredWorks;
  };

  const filteredWorks = getFilteredWorks();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {language === 'zh' ? '音乐作品' : 'Music Works'}
          </h1>
          <p className="text-lg text-gray-600">
            {language === 'zh' ? '发现和购买优质的音乐作品，先行者享受后续返利' : 'Discover and purchase quality music works, pioneers earn referral rewards'}
          </p>
        </div>

        <div className="flex gap-8">
          {/* Left Sidebar */}
          <div className="w-80 flex-shrink-0">
            {/* Search */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={language === 'zh' ? '搜索音乐作品...' : 'Search music works...'}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {language === 'zh' ? '分类' : 'Categories'}
              </h3>
              <div className="space-y-2">
                {categories.map(category => (
                  <div key={category.key}>
                    <button
                      onClick={() => toggleCategory(category.key)}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors bg-gray-50 text-gray-700 hover:bg-gray-100"
                    >
                      <span>{getCategoryLabel(category)}</span>
                      {expandedCategories.includes(category.key) ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </button>
                    
                    {expandedCategories.includes(category.key) && category.children && (
                      <div className="ml-4 mt-2 space-y-1">
                        {category.children.map(child => (
                          <button
                            key={child.key}
                            onClick={() => setSelectedCategory(child.key)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                              selectedCategory === child.key
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {getCategoryLabel(child)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Settings */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <button className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <AdjustmentsHorizontalIcon className="h-5 w-5" />
                <span>{language === 'zh' ? '高级筛选' : 'Advanced Filter'}</span>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {(() => {
                  const category = categories.find(cat => 
                    cat.children?.some(child => child.key === selectedCategory)
                  );
                  const subcategory = category?.children?.find(child => child.key === selectedCategory);
                  if (category && subcategory) {
                    return `${getCategoryLabel(category)} - ${getCategoryLabel(subcategory)}`;
                  }
                  return language === 'zh' ? '所有作品' : 'All Works';
                })()}
              </h2>
              <p className="text-gray-600">
                {language === 'zh' ? `找到 ${filteredWorks.length} 个作品` : `Found ${filteredWorks.length} works`}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredWorks.map(work => (
                <div 
                  key={work.id} 
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                  onClick={() => navigate(`/works/${work.id}`)}
                >
                  <div className="relative h-64 bg-gradient-to-br from-purple-400 to-pink-400 overflow-hidden">
                    {work.coverUrl ? (
                      <img 
                        src={work.coverUrl} 
                        alt={work.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <PlayIcon className="h-20 w-20 text-white" />
                      </div>
                    )}
                    
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                      <button className="opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
                        <div className="bg-white rounded-full p-4 shadow-lg">
                          <PlayIcon className="h-8 w-8 text-blue-600" />
                        </div>
                      </button>
                    </div>

                    <div className="absolute bottom-4 right-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
                      {work.duration}
                    </div>

                    <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {work.genre}
                    </div>

                    <div className="absolute top-4 right-4 flex flex-col gap-2">
                      {(work as any).isPioneer && (work as any).type === 'full-version' && (
                        <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <StarIcon className="w-3 h-3" />
                          {language === 'zh' ? '先行者' : 'Pioneer'}
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        (work as any).type === 'demo' 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-green-500 text-white'
                      }`}>
                        {(work as any).type === 'demo' ? 'Demo' : (language === 'zh' ? '完整版' : 'Full')}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 truncate">{work.title}</h3>
                      <div className="flex items-center space-x-2">
                        <img 
                          src={work.artistAvatar}
                          alt={work.artist}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                        <span className="text-gray-600 text-sm">{work.artist}</span>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                      {work.description}
                    </p>

                    {(work as any).type === 'full-version' && (
                      <div className="mb-4">
                        <span className={`text-lg font-bold ${(work as any).isPioneer ? 'text-purple-600' : 'text-blue-600'}`}>
                          {(work as any).type === 'demo' 
                            ? (language === 'zh' ? '免费试听' : 'Free Preview')
                            : (language === 'zh' ? '起价' : 'From')} HK$ 88
                        </span>
                        {(work as any).isPioneer && (work as any).pioneersCount && (
                          <div className="text-xs text-purple-600 mt-1">
                            {language === 'zh' ? `${(work as any).pioneersCount}位先行者` : `${(work as any).pioneersCount} pioneers`}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <EyeIcon className="h-4 w-4" />
                          <span>{((work.playCount || 0) / 1000).toFixed(1)}k</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <HeartIcon className="h-4 w-4" />
                          <span>{work.likes || 0}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ChatBubbleLeftIcon className="h-4 w-4" />
                          <span>{work.comments || 0}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {(work as any).type === 'demo' ? (
                        <button 
                          className="flex-1 bg-orange-500 text-white py-3 px-4 rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center gap-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            alert(language === 'zh' ? `免费试听 ${work.title}` : `Free preview ${work.title}`);
                          }}
                        >
                          <PlayIcon className="h-5 w-5" />
                          {language === 'zh' ? '免费试听' : 'Free Preview'}
                        </button>
                      ) : (
                        <>
                          <button 
                            className={`flex-1 py-3 px-4 rounded-lg transition-colors font-medium flex items-center justify-center gap-2 ${
                              (work as any).isPioneer
                                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPurchaseModal({ isOpen: true, work });
                            }}
                          >
                            {(work as any).isPioneer ? <StarIcon className="h-4 w-4" /> : <CurrencyDollarIcon className="h-4 w-4" />}
                            {(work as any).isPioneer 
                              ? (language === 'zh' ? '成为先行者' : 'Become Pioneer')
                              : (language === 'zh' ? '购买许可' : 'Buy License')
                            }
                          </button>
                          <button 
                            className={`px-4 py-3 border rounded-lg hover:bg-opacity-10 transition-colors ${
                              (work as any).isPioneer
                                ? 'border-purple-600 text-purple-600 hover:bg-purple-600'
                                : 'border-blue-600 text-blue-600 hover:bg-blue-600'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              alert(language === 'zh' ? `试听 ${work.title}` : `Preview ${work.title}`);
                            }}
                          >
                            <PlayIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {filteredWorks.length === 0 && (
                <div className="col-span-full text-center py-16">
                  <div className="mx-auto h-24 w-24 text-gray-400 mb-4">
                    <PlayIcon className="h-full w-full" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">
                    {language === 'zh' ? '没有找到作品' : 'No works found'}
                  </h3>
                  <p className="text-gray-500">
                    {language === 'zh' ? '尝试调整搜索条件或分类' : 'Try adjusting search terms or categories'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <PurchaseModal 
        isOpen={purchaseModal.isOpen}
        onClose={() => setPurchaseModal({ isOpen: false, work: null })}
        work={purchaseModal.work}
        language={language}
      />
    </div>
  );
};

export default WorksPage;